import {Component, Plugins, ElementPreset} from '@packages/systems/wfdl';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {EElement, EList, ERecord, EEnum} from '@packages/systems/wfdl-ast';
import {
  AddPanelForm64Icon,
  AddPanelPageSlot64Icon,
  ElementFormBlockIcon as FormPlaceholderIcon,
  ElementSlotIcon as PageSlotIcon,
} from '@packages/systems/spring';
import {wrapAddPanelIcon} from '@packages/systems/ui/shared-ui/wrapAddPanelIcon';

import DropTarget from './DropTarget';
import FormPlaceholder from './FormPlaceholder';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {isDynPage} from '@packages/shared/utils/PageUtils';

const pageBuildingPlugin = Plugins.name('PageBuilding');

const PanelPageSlot = wrapAddPanelIcon(AddPanelPageSlot64Icon);
const PanelFormPlaceholder = wrapAddPanelIcon(AddPanelForm64Icon);

const getCategory = ({
  isInsideComponentRoot,
  currentPage,
}: {
  currentPage: PageRecordType;
  isInsideComponentRoot: boolean;
}) => {
  if (isInsideComponentRoot) return null;
  if (isDynPage(currentPage)) return null;

  return 'Structure';
};

/**
 * Webflow plugin related to Page Building Drop Targets.
 */
export const plugin = Plugins.PrivilegedPlugin({
  atoms: {DropTarget, FormPlaceholder},
  icons: {
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    PanelPageSlot,
    PageSlotIcon,
    // @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<Omit<any, "ref"> & RefAttributes<any>>' is not assignable to type 'ComponentType<IconProps>'.
    PanelFormPlaceholder,
    FormPlaceholderIcon,
  },
  elementPresets: {
    // The preset exists so `element_builder` and the Designer Extensions API
    // can create the atom. A null category keeps it out of the Add panel and
    // the Finder, so only campaign page generation inserts it in practice.
    //
    // It is also public API surface on purpose: `element_builder` and
    // `webflow.elementPresets` both expose it, and nothing restricts who can
    // create one. That is acceptable because nobody needs it outside campaign
    // generation, and a hand-made placeholder is harmless: it shows in the
    // Designer and renders nothing when published.
    FormPlaceholder: ElementPreset.create({
      factory: EElement({
        id: 'Form Placeholder',
        type: qualify(pageBuildingPlugin, Component.name('FormPlaceholder')),
        data: ERecord({
          tag: EEnum('div'),
          children: EList([]),
        }),
      }),
      icon: {
        medium: qualify(pageBuildingPlugin, iconName('PanelFormPlaceholder')),
        small: qualify(pageBuildingPlugin, iconName('FormPlaceholderIcon')),
      },
      label: 'Form Placeholder',
      category: () => null,
      helpData: {
        tag: 'div',
        description: [
          'A form placeholder marks where a HubSpot or Marketo form is connected on a campaign page. It renders nothing on the published page until a form is connected.',
        ],
      },
    }),
    DropTarget: ElementPreset.create({
      factory: EElement({
        id: 'Page Slot',
        type: qualify(pageBuildingPlugin, Component.name('DropTarget')),
        data: ERecord({
          tag: EEnum('div'),
          children: EList([]),
        }),
      }),
      icon: {
        medium: qualify(pageBuildingPlugin, iconName('PanelPageSlot')),
        small: qualify(pageBuildingPlugin, iconName('PageSlotIcon')),
      },
      label: 'Page Slot',
      category: getCategory,
      helpData: {
        tag: 'div',
        description: [
          'Page slots give users with the Marketer role and other roles that use build mode designated areas where they can drag and drop components to build pages. Outside of a page slot, these users can only edit content.',
        ],
        videoSrc:
          'https://d3e54v103j8qbb.cloudfront.net/videos/tooltips/page-slots.ceb9cee6c0.mp4',
      },
    }),
  },
});
